import {AfterViewInit, ChangeDetectorRef, Component, OnInit, Output, EventEmitter, ViewChild, forwardRef, Inject} from '@angular/core';

import { StateMap, UnitOfWork, UserManager } from '../../services/common';
import { EditManager, IEditHost, UtilFns } from '../../utils/common';

import * as _ from 'lodash';
import {Observable, Subject} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';

import { Material, MaterialComponent, MaterialComposition } from '../../entities/EntityModels';
import { MaterialComponentCandidate } from '../../entities/projections/MaterialComponentCandidate';
import {NCSManagerData, NCSManagerComponent} from './ncs-manager.component';

@Component({
    selector: 'ncs-upload',
    templateUrl: './ncs-upload.html',
})
export class NCSUploadComponent implements OnInit, AfterViewInit {
    @Output() stateChange = new EventEmitter<string>();

    _documentManagementUrl: string;	// used for uploading a spreadsheet of material components
    _uploadComponentFileURL: string;

    _edm: EditManager<MaterialComposition>;
    _edmComponents: EditManager<MaterialComponent>;

    _isLoading = false;
    _parentData: NCSManagerData;
    _processingMessage: string;
    _validCandidates: MaterialComponentCandidate[] = [];
    _validationMessage: string;

    public _fileData: any;
    public _fileInputElement: any;

    constructor(public _stateMap: StateMap, private _httpClient: HttpClient, public _uow: UnitOfWork, public _userManager: UserManager,
                private changeDetectorRef: ChangeDetectorRef, @Inject(forwardRef(() => NCSManagerComponent)) public _parent: NCSManagerComponent) {
        this._parentData = _parent._data;
    }

    ngOnInit() {
        this._edm           = new EditManager<MaterialComposition>(this, this._uow, this._stateMap, 'Naturals Upload');
        this._edmComponents = new EditManager<MaterialComponent>(this, this._uow, this._stateMap, 'Naturals Upload');

        this._uow.getDocumentManagementUrl().then(url => {
            this._stateMap.documentManagementBaseUrl    = url; // Needs to be set if Material page is opened from an external link.
            this._uploadComponentFileURL                = this._stateMap.documentManagementBaseUrl + '/MaterialData'; //
        });
    }

    ngAfterViewInit() {
        this._fileInputElement = $('#fileInputElement')[0] as HTMLInputElement;
    }

    canDeactivate() {
        return (!this._uow.hasChanges());
    }

    get utilFns() {
        return UtilFns;
    }

    rowBackgroundColor(hasErrors: boolean): any {
        return (hasErrors) ? '#FFF0F5' : '#FFFFFF';
    }

    onApply() {
        const appliedComponents     = this._edmComponents.onApplyCore();
        const appliedCompositions   = this._edm.onApplyCore();

        return (appliedComponents && appliedCompositions);
    }

    canCancel() {
        return (this._parentData.canSaveMaterialComponentCandidates);
    }

    canClear() {
        return (!this._parentData.canSaveMaterialComponentCandidates && this._parentData.materialComponentCandidates != null && this._parentData.materialComponentCandidates.length > 0);
    }

    canSave() {
        return this._parentData.canSaveMaterialComponentCandidates;
    }

    hasCandidateData() {
        return (this._parentData.materialComponentCandidates != null && this._parentData.materialComponentCandidates.length > 0);
    }

    public onCancel() {
        this._fileData = null;
        if (this._fileInputElement != null) {
            this._fileInputElement.value = null;
        }

        this._processingMessage = '';
        this._validationMessage = '';
        this._parentData.materialComponentCandidates = [];

        this._parentData.canSaveMaterialComponentCandidates = false;
    }

    onSave() {
        this._edm.editing           = true;
        this._edmComponents.editing = true;

        this._validCandidates               = this._parentData.materialComponentCandidates.filter(m => m.errorsFound == false);
        const uniqueMaterialIds             = [...new Set( this._validCandidates.map(obj => obj.compositionMaterialId))];
        const uniqueCandidateMaterialIds    = [...new Set( this._validCandidates.map(obj => obj.componentMaterialId))];

        const params = {
            materialIds: uniqueCandidateMaterialIds
        };
        this._uow.fetch('Materials/MaterialComponentsByMaterialIds', params, true).then(r => {
            if (r != null && r.length > 0) { // the result set can be empty if the candidates are all new components.
                this._edmComponents.entities = r;
            }

            // create the new components if needed and compositions
            this.createComponentsAndCompositions();

            if (this.onApply()) {
                return this._edm.onSaveCore().then(() => {         // the uow saves all changes
                    // update the display of the original component candidate data with the latest status.
                    this._parentData.materialComponentCandidates = this.updateCandidateStatusMessage();

                    this._processingMessage = 'The new component data was uploaded successfully.';
                    this._validationMessage = '';

                    this._parentData.canSaveMaterialComponentCandidates = false;
                }).catch((e) => {
                    this._validationMessage = 'An error occurred: ' + e.toString();
                });
            } else {
                this._validationMessage = 'An error occurred preventing the final step of the component data upload.';
            }
        });
    }

    createComponentsAndCompositions() {
        this._edm.entities              = [];
        this._edmComponents.entities    = [];

        this._validCandidates.forEach((c: MaterialComponentCandidate) => {
            // *************************************
            // first find or add components
            // *************************************
            let currentcomponent = this.filterToComponentByMaterialId(c.componentMaterialId);
            if (currentcomponent == null) {
                // make sure the component hasn't been created in this transaction for a different material in the list.
                const idx = this._edmComponents.entities.findIndex(n => n.materialId == c.componentMaterialId);
                if (idx >= 0)  {
                    currentcomponent = this._edmComponents.entities[idx];
                } else { // create new component record
                    currentcomponent                                = this._uow.materialComponentFactory.create();
                    currentcomponent.materialId                     = c.componentMaterialId;
                    currentcomponent.typeMaterialComponentClassId   = 1;

                    this._edmComponents.entities.push(currentcomponent);
                }
            }

            // **************************
            // now add the composition
            // **************************
            const newcomposition = this._uow.materialCompositionFactory.create();
            newcomposition.materialId           = c.compositionMaterialId;
            newcomposition.materialComponent    = currentcomponent;
            newcomposition.componentAmount      = c.componentPercentage;
            newcomposition.validationDate       = c.validationDate;
            newcomposition.itemCode             = c.itemCode;

            this._edm.entities.push(newcomposition);
        });
    }

    updateCandidateStatusMessage(): MaterialComponentCandidate[] {
        const data = _.clone(this._parentData.materialComponentCandidates);

        const processedCandidates: MaterialComponentCandidate[] = data.map((c) => {
            if (c.errorsFound === false) {
                // Return a new object with the updated price
                return { ...c, statusMessage: 'Data added successfully.' };
            }
            return c;
        });

        return processedCandidates;
    }

    filterToCompositionByMaterialId(id: number): MaterialComposition {
        if (this._edm.entities == null || this._edm.entities.length < 1) {
            return null;
        }

        const matcomps = this._edm.entities.filter(mc => mc.materialId == id);
        if (matcomps != null && matcomps.length > 0) {
            return matcomps[0];
        }
    }

    filterToComponentByMaterialId(id: number): MaterialComponent {
        if (this._edmComponents.entities == null || this._edmComponents.entities.length < 1) {
            return null;
        }

        const components = this._edmComponents.entities.filter(mc => mc.materialId == id);
        if (components != null && components.length > 0) {
            return components[0];
        }
    }

    // ************************************************************************
    // *Process uploaded Excel file,
    // ************************************************************************
    async onLoadMaterialComponentsFile(files: FileList) {
        this._fileData = files.item(0);

        this._processingMessage = '';
        this._validationMessage = '';

        this.changeDetectorRef.detectChanges();

        const result = await this.uploadComponentile();
    }

    processComponentUploadResults(data: any) {
        this._parentData.canSaveMaterialComponentCandidates = false;

        if (data.ListData == null) {
            this._processingMessage = 'An error occurred that is preventing the processing of the file data.';
            this._parentData.materialComponentCandidates = [];
            this._isLoading = false;
            return;
        }

        // filter for errors
        this._parentData.materialComponentCandidates = data.ListData.map((item: any) => new MaterialComponentCandidate(item));

        const errors = this._parentData.materialComponentCandidates.filter(m => m.errorsFound);
        if (errors == null || errors.length == 0) {
            this._processingMessage = 'All file entries valid are valid. Click the "Save" button to confirm the components.';
            this._parentData.canSaveMaterialComponentCandidates = true;
            return;
        }

        if (errors.length == this._parentData.materialComponentCandidates.length) {
            this._processingMessage = 'There are no entries in the file that are valid.';
        } else {
            this._processingMessage = 'Some of the entries are valid. Click the "Save" button to add just the valid data.';
            this._parentData.canSaveMaterialComponentCandidates = true;
        }
    }

    rowColor(idx: number) {
        return ((idx % 2) > 0) ? '#F0F8FF' : '#FFFFFF';
    }

    // ************************************************************************
    // web api for uploading an Excel file
    // ************************************************************************
    getUploadComponentFiletURL(): Observable<any> {
        const url = this._uploadComponentFileURL + '/UploadMaterialComponentData';
        const headers = new HttpHeaders();
        headers.append('Accept', 'application/json');

        const formData: FormData = new FormData();
        formData.append('fileName', this._fileData.name);
        formData.append('file', this._fileData);

        return this._httpClient.post<any>(url, formData);
    }

    public uploadComponentile(): Observable<boolean> {
        const result = new Subject<boolean>();

        this._isLoading = true;

        this.getUploadComponentFiletURL()
            .subscribe(
                (response) => {
                    const data = response;

                    if (data.Success == false) {
                        this._validationMessage = data.ExceptionMessage;
                    } else {
                        this._validationMessage = 'File was processed successfully.';
                        result.next(true);
                    }
                    this.processComponentUploadResults(data);

                    this._isLoading = false;
                },
                (error) => {
                    this._validationMessage = error.message;
                    this._isLoading = false;
                });

        return result.asObservable();
    }

    onShowMaterialComponentData(materialId: number) {
        this._parentData.materialId = materialId;
        this.stateChange.emit('view');
    }

    // ************************************************************************
    // *Write component candidate data to a tab delimited text file.
    // ************************************************************************
    public exportResults() {
        const rowData: string[] = [];

        const hData: string[] = ['Constituent MaterialId', 'Constituent Name', 'Constituent CAS No.', 'NCS MaterialId', 'NCS Name', 'Validation Date', 'Constituent Percentage', 'Errors Found', 'Component Is RIFM Material', 'Mixture Is RIFM Material', 'Component Already Assigned to Mixture', 'Status Message'];
        const headerRow = hData.join('\t');

        rowData.push(headerRow);

        this._parentData.materialComponentCandidates.forEach((mc: MaterialComponentCandidate) => {
            const reportDetails: string[]     = [];

            reportDetails.push(mc.componentMaterialId.toString());
            reportDetails.push(mc.componentPrincipalName);
            reportDetails.push(mc.componentCASNumber);
            reportDetails.push(mc.compositionMaterialId.toString());
            reportDetails.push(mc.compositionMaterialPrincipalName);
            reportDetails.push(this.utilFns.formatDate(mc.validationDate));
            reportDetails.push(this.utilFns.formatNumber(mc.componentPercentage, 10, 3));
            // reportDetails.push(mc.componentPercentage.toString());
            reportDetails.push(mc.errorsFound.toString());
            reportDetails.push(mc.componentIsRIFMMaterial.toString());
            reportDetails.push(mc.compositionMaterialIsRIFMMaterial.toString());
            reportDetails.push(mc.componentAlreadyAssignedToMixture.toString());
            reportDetails.push(mc.statusMessage);

            rowData.push(reportDetails.join('\t'));
        });

        const textFileData = rowData.join('\n');
        const blob = new Blob([textFileData], { type: 'text/plain;charset=utf-8;' });

        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'ConstituentCandidateData.txt');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    }
}

